<template>
  <div class="cancel-confirm-pane">
    <h3 class="cancel-confirm-pane__title" v-html="$tc('CancelPage.ConfirmPage.title')"></h3>

    <p class="cancel-confirm-pane__message" v-html="$tc('CancelPage.ConfirmPage.message')" />

    <ul class="cancel-confirm-pane__attention">
      <li>{{ $tc('CancelPage.ConfirmPage.attention01') }}</li>
    </ul>

    <action-buttons-section
      class="cancel-confirm-pane__action-buttons"
      scrollTargetSelector=".cancel-confirm-pane"
      :visible="true"
    >
      <atom-input-button
        :linkText="$tc('CancelPage.ConfirmPage.submit')"
        :isDisabled="false"
        @push="handlerSubmit()"
      />

      <atom-link-button
        :linkText="$tc('common.cancel')"
        :url="mypageUrl"
        :isOpenAsBlank="false"
        colorType="tertiary"
      />
    </action-buttons-section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from '@vue/composition-api'
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash'
import ActionButtonsSection from '@/components/common/form/ActionButtonsSection.vue'
import AtomInputButton from '@/components/atoms/input/AtomInputButton.vue'
import AtomLinkButton from '@/components/atoms/AtomLinkButton.vue'
import useContractInfo from '@/store/hook/useContractInfo'
import LocalStorageAccessor from '@/util/localstorage/LocalStorageAccessor'
import DeviceInfo from '@/util/DeviceInfo'

/**
 * 退会確認ページ ペイン
 */
export default defineComponent({
  name: 'CancelConfirmPane',
  components: {
    ActionButtonsSection,
    AtomInputButton,
    AtomLinkButton,
  },
  setup() {
    // hooks
    const { cancelContract } = useContractInfo()
    const { fetchContractInfo, ownContractInfo } = useContractInfo()

    /**
     * アプリから遷移したかどうか
     */
    const { transitionFromMobileApp } = LocalStorageAccessor
    /**
     * /Androidブラウザで開いている場合はアプリに戻るようにするための条件
     * TODO: ※iPhoneからアプリに戻れないバグの対応が出来次第、「アプリから遷移した場合はアプリに戻る」という挙動にする。
     * 現在はAndroidのみアプリに戻るようにする
     */
    const canReturnToApp = computed(() => transitionFromMobileApp && DeviceInfo.isAndroidBrowser())

    const mypageUrl = computed(() =>
      // アプリから遷移 & Androidブラウザで開いてる場合は、アプリに戻るようにする。
      canReturnToApp.value
        ? `${process.env.VUE_APP_BROWSER_CAN_REDIRECT_TO_APP_BASE_URL}#/mypage`
        : '/mypage',
    )

    // 契約情報を取得
    fetchContractInfo()

    return {
      ownContractInfo,
      cancelContract,
      mypageUrl,
    }
  },
  methods: {
    /**
     * 退会処理
     * ContractInfoDocumentのcancellationDateに日時をセットして送信
     */
    async handlerSubmit() {
      const loader = this.$loading.show()
      const newContractInfo = cloneDeep(this.ownContractInfo)
      newContractInfo.cancellationDate = dayjs().valueOf()
      const cancelContractResult = await this.cancelContract(newContractInfo)
      loader.hide()
      if (!cancelContractResult.isSuccess) {
        await this.$router.replace({ name: 'ErrorPage' })
        return
      }
      this.$router.push('/cancel/complete')
    },
  },
})
</script>

<style lang="scss" scoped>
.cancel-confirm-pane {
  width: 100%;
  max-width: 840px;
  padding: 80px 16px 160px;
  margin: 0 auto;
  text-align: center;
  color: $gray75;
  background: $gray1;
  font-feature-settings: 'palt' on;
  letter-spacing: 0.04em;
  font-family: $fontSignup;

  &__title {
    @include fontSetTextMedium(17);

    line-height: 1.41;
    font-weight: 500;
    color: $red;
  }

  &__message {
    margin-top: 24px;
    @include fontSetTextMedium(13);

    color: $gray75;
    font-weight: 500;
  }

  &__attention {
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    align-content: flex-start;
    justify-items: flex-start;
    justify-content: flex-start;
    gap: 8px;
    width: 100%;
    margin-top: 24px;
    padding: 16px;
    background: #e5e5e5;
    border-radius: 4px;
    text-align: left;
    @include fontSetTextMedium(13);

    color: $gray75;
  }

  &__action-buttons {
    bottom: 0;
  }
}
</style>

<style lang="scss">
.cancel-confirm-pane {
  &__attention {
    li::before {
      display: inline-block;
      content: '・';
    }
  }

  &__action-buttons {
    .action-buttons-section__inner {
      @media screen and (min-width: $responsiveTabletWidth) {
        flex-direction: row-reverse;
      }
    }
  }
}
</style>
